/**
 * Habit Banner — responsive cover with change/reposition controls.
 * Desktop: hover shows Change + Reposition in top-right corner (no darkening).
 * Mobile: 3-dot menu in top-right with dropdown.
 * Supports solid colors, gradients, preset images, and custom uploads.
 */

import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreVertical, ImagePlus, Move } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useAuthStore } from '@turnifly/shared/stores'
import { BannerChangeModal } from './banner-change-modal'
import { BannerRepositionOverlay } from './banner-reposition-overlay'

/* ── Banner data ── */

// eslint-disable-next-line react-refresh/only-export-components
export const SOLID_COLORS = [
  { key: 'color_1', bg: 'bg-[#E16259]' },
  { key: 'color_2', bg: 'bg-[#D4943A]' },
  { key: 'color_3', bg: 'bg-[#4A8FD4]' },
  { key: 'color_4', bg: 'bg-[#E8DFD0]' },
] as const

// eslint-disable-next-line react-refresh/only-export-components
export const GRADIENT_PRESETS = [
  { key: 'gradient_1', bg: 'bg-gradient-to-br from-[#ff6b6b] via-[#feca57] to-[#48dbfb]' },
  { key: 'gradient_2', bg: 'bg-gradient-to-br from-[#a29bfe] via-[#fd79a8] to-[#fdcb6e]' },
  { key: 'gradient_3', bg: 'bg-gradient-to-br from-[#00cec9] via-[#6c5ce7] to-[#e84393]' },
  { key: 'gradient_4', bg: 'bg-gradient-to-br from-[#f7797d] via-[#FBD786] to-[#C6FFDD]' },
  { key: 'gradient_5', bg: 'bg-gradient-to-br from-[#0abcf9] to-[#2c69d1]' },
  { key: 'gradient_6', bg: 'bg-gradient-to-br from-[#e056a0] via-[#db47cf] to-[#8a5cf5]' },
  { key: 'gradient_7', bg: 'bg-gradient-to-br from-[#11998e] to-[#38ef7d]' },
  { key: 'gradient_8', bg: 'bg-gradient-to-br from-[#4834d4] via-[#686de0] to-[#be2edd]' },
] as const

export const PRESET_IMAGE_COUNT = 44

/** Check if template is an image that supports vertical repositioning */
// eslint-disable-next-line react-refresh/only-export-components
export function isImageTemplate(bannerUrl: string | null, template: string): boolean {
  return Boolean(bannerUrl) || template.startsWith('preset_')
}

/** Legacy gradients for backwards compatibility with existing DB values */
const LEGACY_GRADIENTS: Record<string, string> = {
  default_1: 'bg-gradient-to-tr from-[#1B1B29] to-[#2D2D44]',
  default_2: 'bg-gradient-to-r from-[#0f2027] via-[#203a43] to-[#2c5364]',
  default_3: 'bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]',
  default_4: 'bg-gradient-to-r from-[#141e30] to-[#243b55]',
  default_5: 'bg-gradient-to-tr from-[#0a0a0f] via-[#1a0a2e] to-[#16213e]',
  default_6: 'bg-gradient-to-r from-[#232526] to-[#414345]',
}

function getDefaultTemplate(gender: string | null | undefined): string {
  return gender === 'female' ? 'preset_23' : 'preset_4'
}

/* ── Banner Component ── */

export const HabitBanner: React.FC = () => {
  const { user } = useAuthStore()
  const { t } = useTranslation()
  const [isHovered, setIsHovered] = useState(false)
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isChangeModalOpen, setChangeModalOpen] = useState(false)
  const [isRepositioning, setRepositioning] = useState(false)
  const menuBtnRef = useRef<HTMLButtonElement>(null)

  const bannerUrl = user?.banner_url ?? null
  const bannerTemplate = user?.banner_template ?? 'default_1'
  const positionY = user?.banner_position_y ?? 50

  // Show gender-appropriate default if user never changed banner
  const resolvedTemplate = (bannerTemplate === 'default_1' && !bannerUrl)
    ? getDefaultTemplate(user?.gender)
    : bannerTemplate

  const handleChange = useCallback((): void => {
    setMobileMenuOpen(false)
    setChangeModalOpen(true)
  }, [])

  const handleReposition = useCallback((): void => {
    setMobileMenuOpen(false)
    setRepositioning(true)
  }, [])

  if (isRepositioning) {
    return (
      <BannerRepositionOverlay
        bannerUrl={bannerUrl}
        bannerTemplate={resolvedTemplate}
        initialPositionY={positionY}
        onClose={() => setRepositioning(false)}
      />
    )
  }

  return (
    <>
      <div
        className="relative w-full h-[160px] lg:h-[200px] rounded-2xl overflow-hidden border border-border group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Banner content */}
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: `center ${positionY}%` }}
            draggable={false}
          />
        ) : (
          <BannerPresetBackground template={resolvedTemplate} positionY={positionY} />
        )}

        {/* ── Desktop: buttons in top-right on hover (no overlay) ── */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="absolute top-2.5 right-2.5 z-10 hidden lg:flex items-center gap-1.5"
            >
              <button
                onClick={handleChange}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/40 backdrop-blur-sm text-white/90 text-[12px] font-medium hover:bg-black/60 transition-colors"
              >
                <ImagePlus size={13} />
                {t('home.bannerChange')}
              </button>
              {isImageTemplate(bannerUrl, resolvedTemplate) && (
                <button
                  onClick={handleReposition}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/40 backdrop-blur-sm text-white/90 text-[12px] font-medium hover:bg-black/60 transition-colors"
                >
                  <Move size={13} />
                  {t('home.bannerReposition')}
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Mobile: 3-dot menu ── */}
        <div className="absolute top-2.5 right-2.5 z-10 lg:hidden">
          <button
            ref={menuBtnRef}
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-sm text-white/70 hover:text-white transition-colors tap-highlight-transparent"
          >
            <MoreVertical size={16} />
          </button>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setMobileMenuOpen(false)} />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -4 }}
                  transition={{ duration: 0.12 }}
                  className="absolute top-10 right-0 z-30 bg-surface border border-border rounded-xl overflow-hidden min-w-[160px] shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
                >
                  <button
                    onClick={handleChange}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-text hover:bg-white/[0.06] transition-colors"
                  >
                    <ImagePlus size={14} className="text-text-hint" />
                    {t('home.bannerChange')}
                  </button>
                  {isImageTemplate(bannerUrl, resolvedTemplate) && (
                    <>
                      <div className="h-px bg-border mx-2" />
                      <button
                        onClick={handleReposition}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-text hover:bg-white/[0.06] transition-colors"
                      >
                        <Move size={14} className="text-text-hint" />
                        {t('home.bannerReposition')}
                      </button>
                    </>
                  )}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {isChangeModalOpen && (
        <BannerChangeModal onClose={() => setChangeModalOpen(false)} />
      )}
    </>
  )
}

/* ── Preset background renderer ── */

interface BannerPresetProps {
  template: string
  positionY?: number
}

export const BannerPresetBackground: React.FC<BannerPresetProps> = ({ template, positionY = 50 }) => {
  // Solid color
  const color = SOLID_COLORS.find((c) => c.key === template)
  if (color) return <div className={`absolute inset-0 ${color.bg}`} />

  // Gradient
  const gradient = GRADIENT_PRESETS.find((g) => g.key === template)
  if (gradient) return <div className={`absolute inset-0 ${gradient.bg}`} />

  // Preset image
  if (template.startsWith('preset_')) {
    const id = template.replace('preset_', '')
    return (
      <img
        src={`/banners/${id}.jpg`}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: `center ${positionY}%` }}
        draggable={false}
      />
    )
  }

  // Legacy gradient fallback
  const legacyClass = LEGACY_GRADIENTS[template] ?? LEGACY_GRADIENTS.default_1
  return <div className={`absolute inset-0 ${legacyClass}`} />
}
